import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import { InlineEditableTextfield } from '@atlaskit/inline-edit';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	CONFLUENCE_PAGE_ADD_LABEL_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import type { ConfluencePageOption } from '@atlassian/rovo-platform-ui-components/base-page-selector/types';
import { ConfluencePageSelector } from '@atlassian/rovo-platform-ui-components/confluence-page-selector';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

export const filterConfluencePageAddLabelActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.ConfluencePageAddLabel]>[] => {
	const filteredActions = actions.filter(assertIsConfluencePageAddLabelAction);
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.ConfluencePageAddLabel]
	>[];
};

const assertIsConfluencePageAddLabelAction = (action: MessageAction): MessageAction => {
	if (action.key !== CONFLUENCE_PAGE_ADD_LABEL_KEY) {
		throw new Error('Invalid action key for ConfluencePageAddLabelAction');
	}
	return action;
};

export const ConfluencePageAddLabelAction = ({
	onCustomDataConfirm,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.ConfluencePageAddLabel>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const firstAction = actions[0];
	const { data } = firstAction;
	const [labelName, setLabelName] = useState(data.name);
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const { getEntity } = useConfluenceEntities(cloudId);
	const [selectedEntity, setSelectedEntity] = useState<ConfluencePageOption | undefined>(undefined);
	const isDisabled = status !== 'idle' || readonly || !selectedEntity || !labelName;
	const isSubmitting = status === 'started';

	useEffect(() => {
		const fetchPage = async () => {
			if (data.id) {
				const entity = await getEntity(data.id, 'page');

				if (entity) {
					setSelectedEntity({
						label: entity.name,
						value: entity.id,
						type: 'page',
					});
				}
			}
		};

		fetchPage();
	}, [data.id, getEntity]);

	const handleSubmit = useCallback(() => {
		const mergedData = {
			...data,
			name: labelName,
			id: selectedEntity?.value,
			contentType: selectedEntity?.type ?? 'page',
		};

		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const plannedActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluencePageAddLabelRenderer',
				},
				confirmed_tools: {
					message_id: messageId,
					tools: plannedActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', mergedData)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onCustomDataConfirm(firstAction, mergedData);
	}, [
		onCustomDataConfirm,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		data,
		labelName,
		selectedEntity?.value,
		selectedEntity?.type,
		firstAction,
		actions,
		getStatus,
	]);

	return (
		<Stack space="space.250" testId="confluence-page-add-label-action">
			<Text>
				{formatMessage(messages.actionMessage, {
					name: selectedEntity?.label ?? '',
					bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				})}
			</Text>
			<ActionCardWrapper title="Add Label" product="confluence">
				<Stack space="space.100">
					<Inline space="space.100" alignBlock="center">
						<Heading size="xsmall">{formatMessage(messages.pageSelectorLabel)}</Heading>

						{!selectedEntity ? (
							<Skeleton
								height={30}
								width={200}
								borderRadius={token('radius.large')}
								testId="confluence-page-add-label-page-selector-skeleton"
							/>
						) : (
							<ConfluencePageSelector
								cloudId={cloudId}
								testId="confluence-page-add-label-page-selector"
								contentTypes={['page', 'blogPost']}
								isDisabled={isDisabled}
								onMenuOpen={() => {
									sendAnalyticsEvent({
										action: 'opened',
										actionSubject: 'dropdown',
										actionSubjectId: 'rovoConfluenceAddLabelPageSelector',
									});
								}}
								isRequired
								onChange={(option: ConfluencePageOption | null) => {
									if (option) {
										setSelectedEntity(option);
										sendAnalyticsEvent({
											action: 'selected',
											actionSubject: 'dropdown',
											actionSubjectId: 'rovoConfluenceAddLabelPageSelector',
										});
									}
								}}
								value={selectedEntity}
							/>
						)}
					</Inline>
					<Box>
						{isDisabled ? (
							<Box>{labelName}</Box>
						) : (
							<InlineEditableTextfield
								testId="confluence-page-add-label-name-input-field"
								isRequired
								isCompact
								readViewFitContainerWidth
								defaultValue={labelName}
								label={formatMessage(messages.labelNameLabel)}
								onConfirm={(value) => {
									setLabelName(value);
									sendAnalyticsEvent({
										action: 'edited',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceAddLabel',
									});
								}}
								onCancel={() => {
									sendAnalyticsEvent({
										action: 'cancelled',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceAddLabel',
									});
								}}
								placeholder={formatMessage(messages.labelPlaceholder)}
							/>
						)}
					</Box>
				</Stack>
				<ActionCardFooter>
					<Button
						appearance="primary"
						isDisabled={isDisabled}
						isLoading={isSubmitting}
						onClick={handleSubmit}
					>
						{formatMessage(messages.addLabelButton)}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};
